import React, { useState } from "react";
import {
  List,
  DataTable,
  TextField,
  NumberField,
  Show,
  Pagination,
  ReferenceArrayField,
  ReferenceField,
  TextInput,
  Create,
  SimpleForm,
  SelectInput,
  ReferenceInput,
  CheckboxGroupInput,
  ReferenceArrayInput,
  TopToolbar,
  FilterButton,
  ColumnsButton,
  Button,
  useRecordContext,
  useListContext,
  TabbedShowLayout,
  Tab,
  Labeled,
  useNotify,
} from "react-admin";

// Lib
import { custInfo } from "../lib/customer";
import { DetailsButton } from "../lib/detailsButton";
import { ChatBubbleTooltip } from "../lib/chatBubbleTooltip";
import { ContextTagField } from "../lib/contextTagField";
import { CustomDatetimeField, CustomDateTimeInput } from "../lib/datetime";
import { AliasOrBarcodeReference } from "../lib/aliasWithBarcodeTooltip";
import RangePickerInput from "../lib/dateRangePicker";
import { DownloadCSV } from "../lib/csvDownload";
import { ResetExpandedRows } from "../lib/resetExpandedRows";
import { LotNameField } from "./lots";
import VCScatterPlot from "../charts/virtualComposite";
import VCSparkline from "../charts/vcSparkline";

// MUI
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconDownload from "@mui/icons-material//CloudDownload";
import IconCancel from "@mui/icons-material//Cancel";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import GridLegacy from "@mui/material/GridLegacy";
import Stack from "@mui/material/Stack";

//Other
import moment from "moment";
import { CustomTab } from "../lib/customTab";

// MUI v7's <Grid> dropped the legacy item/xs props (now GridLegacy), and the
// runtime ignores them, so this cast keeps today's markup/rendering intact
// until the layout is migrated to the size={{ xs: N }} API.
// SAFETY: the value is the v7 Grid itself; only its prop type is widened to
// the legacy Grid's so the existing item/xs markup type-checks unchanged.
const LegacyPropsGrid = Grid as unknown as typeof GridLegacy;

const VirtualCompositeAppBarTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span> {"Lot " + record.lotName + " Virtual Composite"}</span>;
};

const VirtualCompositeExportButton = () => {
  const [showDialog, setShowDialog] = useState(false);
  const { sort, filterValues } = useListContext();
  const notify = useNotify();

  // Form options
  const [includeIndividualMeasurements, setIncludeIndividualMeasurements] =
    useState(false);

  const handleDownload = async () => {
    setShowDialog(false);

    try {
      await DownloadCSV(
        "BarrelWise_Virtual_Composites",
        custInfo.customerID,
        "virtual_composite",
        sort,
        filterValues,
        { includeMeasurements: includeIndividualMeasurements },
      );
    } catch (err) {
      console.log(err);
      notify(`Failed to download CSV: ${err}`, { type: "error" });
    }
  };

  return (
    <>
      <Button
        onClick={() => {
          setShowDialog(true);
        }}
        label="Export"
      >
        <IconDownload />
      </Button>
      <Dialog fullWidth open={showDialog}>
        <DialogTitle>CSV Export</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeIndividualMeasurements}
                onChange={(ev) => {
                  setIncludeIndividualMeasurements(ev.target.checked);
                }}
                name="includeIndividualMeasurements"
                color="primary"
              />
            }
            label="Include individual measurements"
          />
        </DialogContent>
        <DialogActions>
          <Button
            label="ra.action.cancel"
            variant="outlined"
            onClick={() => {
              setShowDialog(false);
            }}
          >
            <IconCancel />
          </Button>
          <Button label="Download" variant="contained" onClick={handleDownload}>
            <IconDownload />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const virtualCompositesFilters = [
  <TextInput label="Lot" source="lotName" alwaysOn resettable />,
  <TextInput source="intendedUse" label="Intended Use" resettable />,
  <RangePickerInput source="dateRange" label="Date" />,
];

const VirtualCompositeListActions = () => {
  return (
    <TopToolbar>
      <FilterButton />
      <VirtualCompositeExportButton />
      <ColumnsButton />
    </TopToolbar>
  );
};

// Shown when a virtual composite row is expanded: the vessel measurements
// the composite was calculated from.
export const VirtualCompositeMeasurementsPanel = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <ReferenceArrayField
      reference="events/fso2_reading"
      source="eventFSO2ReadIDs"
    >
      <DataTable
        bulkActionButtons={false}
        rowClick={false}
        sx={{
          marginLeft: "4em",
          width: "fit-content",
          maxWidth: "100%",
        }}
      >
        <DataTable.Col source="barrelID" label="Vessel" disableSort>
          <AliasOrBarcodeReference
            source="barrelID"
            reference="vessels"
            sortable={false}
          />
        </DataTable.Col>
        <DataTable.Col source="fso2" label="Free SO₂ (ppm)" disableSort>
          <NumberField
            source="fso2"
            label={"Free SO\u2082 (ppm)"}
            options={{ maximumFractionDigits: 0 }}
          />
        </DataTable.Col>
        <DataTable.Col source="datetime" label="Time" disableSort>
          <CustomDatetimeField source="datetime" label="Measurement Time" />
        </DataTable.Col>
        <DataTable.Col source="notes" label="Notes" disableSort>
          <ChatBubbleTooltip source="notes" />
        </DataTable.Col>
        <DataTable.Col disableSort>
          <DetailsButton />
        </DataTable.Col>
      </DataTable>
    </ReferenceArrayField>
  );
};

export const VirtualCompositeList = () => (
  <List
    perPage={100}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    filters={virtualCompositesFilters}
    actions={<VirtualCompositeListActions />}
    sort={{ field: "creationTime", order: "DESC" }}
    storeKey={false}
    empty={false}
  >
    <ResetExpandedRows />
    <DataTable
      bulkActionButtons={false}
      rowClick={false}
      expand={<VirtualCompositeMeasurementsPanel />}
    >
      <DataTable.Col source="lotID" label="Lot">
        <ReferenceField source="lotID" reference="lots" link="show">
          <LotNameField />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="blendID" label="Blend">
        <ReferenceField source="blendID" reference="blends" link="show">
          <LotNameField />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="intendedUse" label="Intended Use">
        <TextField label="Intended Use" source="intendedUse" />
      </DataTable.Col>
      <DataTable.Col source="context" label="Context">
        <ContextTagField source="context" />
      </DataTable.Col>
      <DataTable.Col source="measurements" label="Measurements" disableSort>
        <VCSparkline label="Measurements" textAlign="center" />
      </DataTable.Col>
      <DataTable.Col source="fso2Avg" label="Avg.">
        <NumberField
          source="fso2Avg"
          label={"Avg"}
          options={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
        />
      </DataTable.Col>
      <DataTable.Col source="barrelCount" label="Vessels">
        <NumberField source="barrelCount" label="Vessels" />
      </DataTable.Col>
      <DataTable.Col source="creationTime" label="Submission Time">
        <CustomDatetimeField source="creationTime" label="Submission Time" />
      </DataTable.Col>
      <DataTable.Col source="message" label="Notes" disableSort>
        <ChatBubbleTooltip source="message" label="Notes" />
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
    </DataTable>
  </List>
);

const VirtualCompositeOverviewTab = () => {
  return (
    <>
      <Stack spacing={1}>
        <Labeled>
          <ReferenceField
            label="Lot"
            source="lotID"
            reference="lots"
            sortable={false}
            link="show"
          >
            <LotNameField />
          </ReferenceField>
        </Labeled>
        <Labeled>
          <ReferenceField
            label="Blend"
            source="blendID"
            reference="blends"
            sortable={false}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        </Labeled>
        <Labeled>
          <ReferenceField
            label="Intended Use"
            source="lotID"
            reference="lots"
            sortable={false}
            link={false}
          >
            <TextField source="intendedUse" emptyText={"\u00A0"} />
          </ReferenceField>
        </Labeled>
        <Labeled>
          <ContextTagField source="context" />
        </Labeled>
        <Labeled>
          <NumberField
            source="fso2Avg"
            label={"Avg. Free SO\u2082 (ppm)"}
            sortable={false}
            options={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
          />
        </Labeled>
        <Labeled>
          <NumberField source="barrelCount" label="Vessels" sortable={false} />
        </Labeled>
        <Labeled>
          <CustomDatetimeField source="creationTime" label="Submission Time" />
        </Labeled>
        <Labeled>
          <TextField source="notes" label="Notes" sortable={false} />
        </Labeled>
      </Stack>
    </>
  );
};

export const VirtualCompositeShow = () => (
  <Show title={<VirtualCompositeAppBarTitle />}>
    <TabbedShowLayout>
      <Tab label="Summary">
        <Grid container spacing={6}>
          <LegacyPropsGrid item xs={3}>
            <VirtualCompositeOverviewTab />
          </LegacyPropsGrid>
          <LegacyPropsGrid item xs={4}>
            <VCScatterPlot />
          </LegacyPropsGrid>
        </Grid>
      </Tab>
      <Tab label="Measurement">
        <ReferenceArrayField
          label={"Free SO\u2082 (ppm)"}
          reference="events/fso2_reading"
          source="eventFSO2ReadIDs"
        >
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="barrelID" label="Vessel" disableSort>
              <AliasOrBarcodeReference
                source="barrelID"
                reference="vessels"
                sortable={false}
              />
            </DataTable.Col>
            <DataTable.Col source="datetime" label="Measurement Time">
              <CustomDatetimeField source="datetime" label="Measurement Time" />
            </DataTable.Col>
            <DataTable.Col source="fso2" label="Free SO₂ (ppm)" disableSort>
              <NumberField
                source="fso2"
                label={"Free SO\u2082 (ppm)"}
                options={{ maximumFractionDigits: 0 }}
              />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceArrayField>
      </Tab>
      <CustomTab />
    </TabbedShowLayout>
  </Show>
);

const UnassociatedFSO2List = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <ReferenceArrayInput
      source="eventFSO2ReadIDs"
      reference="events/fso2_reading"
      filter={{ lotID: record.lotID, valid: true, inVC: false }}
      label=""
      perPage={1000}
    >
      <CheckboxGroupInput
        translateChoice={false}
        optionText={(record) =>
          `${record.barcode} - ${record.fso2} ppm - ${moment(
            new Date(record.datetime),
          ).format("LLLL")}`
        }
        row={false}
      />
    </ReferenceArrayInput>
  );
};

export const VirtualCompositeCreate = () => (
  <Create redirect="list">
    <SimpleForm>
      <CustomDateTimeInput
        label="Creation Time"
        source="creationTime"
        defaultValue={new Date()}
        readOnly
      />
      <ReferenceInput source="lotID" reference="lots">
        <SelectInput optionText="name" readOnly label="Lot" />
      </ReferenceInput>
      <SelectInput
        source="context"
        choices={[
          { id: "before addition", name: "Before Addition" },
          { id: "spotcheck after add", name: "Spotcheck After Add" },
        ]}
      />
      <TextInput source="message" label="Notes" multiline={true} />
      <UnassociatedFSO2List />
    </SimpleForm>
  </Create>
);
